/**
 * Bridge Shopify's cookie banner actions to the Customer Privacy API so
 * behavioral tracking only starts after explicit consent is recorded.
 */
(function () {
  const CONSENT_RETRY_LIMIT = 20;
  const CONSENT_RETRY_DELAY = 250;
  const RELOAD_STORAGE_KEY = 'theme_shopify_consent_reload';
  let pendingConsent = null;

  function getCustomerPrivacyApi() {
    return window.Shopify &&
      window.Shopify.customerPrivacy &&
      typeof window.Shopify.customerPrivacy.setTrackingConsent === 'function'
      ? window.Shopify.customerPrivacy
      : null;
  }

  function dispatchConsentSynced(consent) {
    document.dispatchEvent(
      new CustomEvent('theme:shopify-consent-synced', {
        detail: { consent: consent }
      })
    );
  }

  function canUseSessionStorage() {
    try {
      return Boolean(window.sessionStorage);
    } catch (error) {
      return false;
    }
  }

  function getReloadMarker() {
    if (!canUseSessionStorage()) {
      return null;
    }

    return window.sessionStorage.getItem(RELOAD_STORAGE_KEY);
  }

  function setReloadMarker(value) {
    if (!canUseSessionStorage()) {
      return;
    }

    window.sessionStorage.setItem(RELOAD_STORAGE_KEY, value);
  }

  function clearReloadMarker() {
    if (!canUseSessionStorage()) {
      return;
    }

    window.sessionStorage.removeItem(RELOAD_STORAGE_KEY);
  }

  function buildConsentFingerprint(consent) {
    return JSON.stringify({
      marketing: consent.marketing,
      analytics: consent.analytics,
      preferences: consent.preferences,
      sale_of_data: consent.sale_of_data
    });
  }

  function shouldReloadAfterConsent(consent) {
    if (!document.getElementById('shopify-pc__banner')) {
      return false;
    }

    const consentFingerprint = buildConsentFingerprint(consent);

    if (getReloadMarker() === consentFingerprint) {
      clearReloadMarker();
      return false;
    }

    setReloadMarker(consentFingerprint);
    return true;
  }

  function reloadAfterConsent() {
    window.setTimeout(function () {
      window.location.reload();
    }, 150);
  }

  function applyConsent(consent) {
    const customerPrivacy = getCustomerPrivacyApi();

    if (!customerPrivacy) {
      return false;
    }

    customerPrivacy.setTrackingConsent(consent, function () {
      dispatchConsentSynced(consent);

      if (shouldReloadAfterConsent(consent)) {
        reloadAfterConsent();
      }
    });

    return true;
  }

  function scheduleConsentRetry(consent, attempt) {
    if (applyConsent(consent) || attempt >= CONSENT_RETRY_LIMIT) {
      return;
    }

    window.setTimeout(function () {
      scheduleConsentRetry(consent, attempt + 1);
    }, CONSENT_RETRY_DELAY);
  }

  function queueConsent(consent) {
    pendingConsent = consent;

    window.setTimeout(function () {
      if (!pendingConsent) {
        return;
      }

      const nextConsent = pendingConsent;
      pendingConsent = null;

      if (!applyConsent(nextConsent)) {
        scheduleConsentRetry(nextConsent, 0);
      }
    }, 0);
  }

  function getActionButton(target) {
    return target && typeof target.closest === 'function'
      ? target.closest('button, [role="button"], input[type="button"], input[type="submit"]')
      : null;
  }

  function getActionMetadata(element) {
    return [
      element.id,
      element.className,
      element.name,
      element.value,
      element.getAttribute('aria-label'),
      element.textContent
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();
  }

  function isShopifyConsentElement(element) {
    return Boolean(
      element &&
      typeof element.closest === 'function' &&
      element.closest('#shopify-pc__banner, [id^="shopify-pc__"], [class*="shopify-pc__"]')
    );
  }

  function classifyAction(target) {
    const button = getActionButton(target);

    if (!button || !isShopifyConsentElement(button)) {
      return null;
    }

    const metadata = getActionMetadata(button);

    if (
      metadata.indexOf('btn-accept') !== -1 ||
      metadata.indexOf('accept all') !== -1 ||
      metadata.indexOf('allow all') !== -1 ||
      /\baccept\b/.test(metadata)
    ) {
      return { type: 'accept' };
    }

    if (
      metadata.indexOf('btn-decline') !== -1 ||
      metadata.indexOf('decline all') !== -1 ||
      metadata.indexOf('reject all') !== -1 ||
      /\bdecline\b/.test(metadata) ||
      /\breject\b/.test(metadata)
    ) {
      return { type: 'decline' };
    }

    if (
      metadata.indexOf('save') !== -1 ||
      metadata.indexOf('confirm my choices') !== -1 ||
      metadata.indexOf('confirm choices') !== -1
    ) {
      return { type: 'save' };
    }

    return null;
  }

  function inferConsentKey(input) {
    const metadata = [
      input.id,
      input.name,
      input.value,
      input.getAttribute('aria-label'),
      input.getAttribute('data-purpose')
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase();

    if (metadata.indexOf('marketing') !== -1) {
      return 'marketing';
    }

    if (metadata.indexOf('analytics') !== -1) {
      return 'analytics';
    }

    if (metadata.indexOf('preferences') !== -1) {
      return 'preferences';
    }

    if (metadata.indexOf('sale') !== -1 || metadata.indexOf('data') !== -1) {
      return 'sale_of_data';
    }

    return null;
  }

  function readConsentFromControls() {
    const controls = document.querySelectorAll(
      '[id^="shopify-pc__"] input[type="checkbox"], [id^="shopify-pc__"] input[type="radio"], [class*="shopify-pc__"] input[type="checkbox"], [class*="shopify-pc__"] input[type="radio"]'
    );
    const consent = {};

    controls.forEach(function (input) {
      const consentKey = inferConsentKey(input);

      if (!consentKey) {
        return;
      }

      if (input.type === 'radio') {
        if (!input.checked) {
          return;
        }

        const metadata = getActionMetadata(input);
        consent[consentKey] = !/(deny|decline|false|no|off|0|reject)/.test(metadata);
        return;
      }

      consent[consentKey] = Boolean(input.checked);
    });

    return Object.keys(consent).length ? consent : null;
  }

  function syncConsentFromAction(action) {
    if (action.type === 'accept') {
      queueConsent({
        marketing: true,
        analytics: true,
        preferences: true
      });
      return;
    }

    if (action.type === 'decline') {
      queueConsent({
        marketing: false,
        analytics: false,
        preferences: false
      });
      return;
    }

    if (action.type === 'save') {
      const granularConsent = readConsentFromControls();

      if (granularConsent) {
        queueConsent(granularConsent);
      }
    }
  }

  document.addEventListener(
    'click',
    function (event) {
      const action = classifyAction(event.target);

      if (action) {
        syncConsentFromAction(action);
      }
    },
    true
  );
})();
